/**
 * PuestaEnMarcha.tsx — qué le falta a esta empresa, dicho al cliente.
 *
 * La lista existía desde E6.3 y **solo la veía el operador**: nosotros
 * sabíamos qué le faltaba al cliente y el cliente no. Entraba por primera vez,
 * veía un tablero en ceros, y no tenía forma de distinguir «me falta cargar la
 * apertura» de «así se ve una empresa nueva». Preguntaba — que es justo la
 * llamada que E8 venía a quitar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ SE VA SOLA CUANDO SOBRA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Si los cinco pasos están cumplidos, esto no se dibuja. No es una pantalla
 * que se visita: es un andamio, y un andamio que se queda puesto después de
 * terminar la obra deja de leerse — a los tres meses nadie lo mira, y el día
 * que vuelva a aparecer algo pendiente tampoco.
 *
 * Por eso vive en el tablero y no en una ruta propia: lo primero que el
 * cliente ve al entrar, mientras haga falta, y nada después.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ CADA PASO DICE DE QUIÉN ES, Y ESO ES LA MITAD DEL VALOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `quien` viene de la base. Un paso que le toca a Skoped Labs —la cédula real,
 * que el cliente no puede corregir desde acá— se ve distinto de uno suyo. Sin
 * esa separación, la lista angustia en vez de servir: son cinco cosas
 * pendientes y no se sabe cuáles puede uno resolver.
 *
 * Y los que sí le tocan llevan a dónde se resuelven. Una lista de pendientes
 * sin camino a la acción obliga a buscar en el menú, y quien está en su primer
 * día no conoce el menú.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIdioma } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import { traerMiPuestaEnMarcha, type PasoDePuestaEnMarcha } from '../datos/libros.ts';
import type { Clave } from '../nucleo/idioma.ts';

/** A dónde se resuelve cada paso, cuando le toca al cliente. */
const DONDE: Readonly<Record<string, { ruta: string; rotulo: Clave }>> = {
  primer_acceso: { ruta: '/usuarios', rotulo: 'pmc.goUsers' },
  entro_alguien: { ruta: '/usuarios', rotulo: 'pmc.goUsers' },
  apertura_cargada: { ruta: '/apertura', rotulo: 'pmc.goOpening' },
};

export function PuestaEnMarcha() {
  const { t } = useIdioma();
  const { pedir, sesion } = useSesion();
  const empresaId = sesion?.empresa.id ?? null;

  const [pasos, setPasos] = useState<PasoDePuestaEnMarcha[] | null>(null);

  const mirar = useCallback(async () => {
    try {
      setPasos(await traerMiPuestaEnMarcha(pedir));
    } catch {
      // ▶ Callado a propósito, y es la única vez que se hace en esta casa.
      //
      // Esto es un andamio sobre el tablero. Si no se puede leer, lo correcto
      // es no dibujarlo: un panel de error encima de los números del negocio
      // asusta más de lo que informa, y lo que falla acá no impide trabajar.
      // Lo que sí falla de verdad ya lo dice el propio tablero.
      setPasos([]);
    }
  }, [pedir]);

  useEffect(() => { if (empresaId) void mirar(); }, [empresaId, mirar]);

  if (pasos === null) return null;

  const faltan = pasos.filter((p) => !p.cumplido);
  if (faltan.length === 0) return null;   // terminó: el andamio se quita

  // Solo los del cliente llevan un porqué escrito: `empresa_creada` nunca
  // aparece —está cumplido siempre— y los nuestros se enseñan juntos, con una
  // sola frase. Escribirles un `pmc.why.*` a los dos habría dejado dos claves
  // que no lee nadie, que es lo que el control de esta semana persigue.
  const mios = faltan.filter((p) => p.quien === 'contador');
  const nuestros = faltan.filter((p) => p.quien !== 'contador');

  return (
    <section className="card" style={{ marginBottom: 'var(--s6)' }}>
      <div className="card__head">
        <h3>{t('pmc.title')}</h3>
        <span className="spacer" />
        <span className="chip chip--warn">
          {pasos.length - faltan.length} / {pasos.length}
        </span>
      </div>
      <p className="small muted">{t('pmc.sub')}</p>

      <div className="stack" style={{ gap: 'var(--s4)', marginTop: 'var(--s5)' }}>
        {mios.map((p) => (
          <div key={p.paso} className="row row--tight">
            <span>
              <strong>{t(`pmc.step.${p.paso}` as Clave)}</strong>
              <br />
              <span className="small muted">{t(`pmc.why.${p.paso}` as Clave)}</span>
            </span>
            <span className="spacer" />
            {DONDE[p.paso] && (
              <Link className="btn btn--secondary btn--sm" to={DONDE[p.paso]!.ruta}>
                {t(DONDE[p.paso]!.rotulo)}
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* ── Lo que NO le toca al cliente, separado y sin botón ── */}
      {nuestros.length > 0 && (
        <div className="alert alert--info" style={{ marginTop: 'var(--s6)' }}>
          <span>
            <strong>{t('pmc.ours')}</strong>{' '}
            {nuestros.map((p) => t(`pmc.step.${p.paso}` as Clave)).join(' · ')}
            {' — '}{t('pmc.oursBody')}
          </span>
        </div>
      )}
    </section>
  );
}
